<script lang="ts">
  type SelectOption = { value: string; label: string };

  let {
    label,
    value = $bindable(''),
    options,
    placeholder = 'Select...',
    disabled = false,
    error = '',
    required = false
  }: {
    label: string;
    value?: string;
    options: string[] | SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    error?: string;
    required?: boolean;
  } = $props();

  const selectId = `select-${Math.random().toString(36).substr(2, 9)}`;

  // Normalize options to always be in { value, label } format
  const normalizedOptions = $derived<SelectOption[]>(
    Array.isArray(options) && options.length > 0 && typeof options[0] === 'string'
      ? (options as string[]).map(opt => ({ value: opt, label: opt }))
      : (options as SelectOption[])
  );
</script>

<div class="form-select">
  <label for={selectId}>
    {label}
    {#if required}
      <span class="required">*</span>
    {/if}
  </label>
  <select
    id={selectId}
    {disabled}
    {required}
    bind:value
    class:error={error}
  >
    {#if placeholder}
      <option value="" disabled selected>{placeholder}</option>
    {/if}
    {#each normalizedOptions as option}
      <option value={option.value}>{option.label}</option>
    {/each}
  </select>
  {#if error}
    <span class="error-message">{error}</span>
  {/if}
</div>

<style lang="scss">
  .form-select {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);

    label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-secondary);

      .required {
        color: var(--color-danger);
        margin-left: var(--spacing-xs);
      }
    }

    select {
      padding: var(--spacing-sm) var(--spacing-md);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      font-size: var(--font-size-sm);
      background: var(--color-background);
      cursor: pointer;
      transition: all var(--transition-fast);

      &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-focus-ring);
      }

      &:hover:not(:disabled) {
        border-color: var(--color-text-muted);
      }

      &:disabled {
        background: var(--color-background-tertiary);
        cursor: not-allowed;
        opacity: 0.6;
      }

      &.error {
        border-color: var(--color-danger);

        &:focus {
          box-shadow: var(--shadow-focus-ring-danger);
        }
      }
    }

    .error-message {
      font-size: var(--font-size-xs);
      color: var(--color-danger);
      margin-top: var(--spacing-xs);
    }
  }
</style>
